import {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import clsx from 'clsx'
import {initialValues} from './_module'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {useAppDispatch, useAppSelector} from '../../../redux/hooks'
import Loader from 'app/custom/loader/Loader'
import {showErrorMessage} from '_metronic/helpers/swalHelper'
import CustomSelect from 'app/custom/CustomSelect'
import {Genders, GendersFr, hearAbout, Prefix} from '_metronic/helpers/jsonData'
import {toAbsoluteUrl} from '_metronic/helpers'
import DatePicker, {DateObject} from 'react-multi-date-picker'
import gregorian from 'react-date-object/calendars/gregorian'
import i18n from 'i18n'
import {GetOnlineCourses, StoreOnlineCourseApp} from '../../../redux/Slice/generalSlice'
import ShowSuccessMsg from './ShowSuccessMsg'
import 'react-phone-input-2/lib/bootstrap.css'

type ConsentKey =
  | 'accurateInformation'
  | 'personalDataProcessing'
  | 'enrolmentTerms'
  | 'equipmentAndInternet'

const initialConsents: Record<ConsentKey, boolean> = {
  accurateInformation: false,
  personalDataProcessing: false,
  enrolmentTerms: false,
  equipmentAndInternet: false,
}

const SenegalCourseApplication = () => {
  const {t} = useTranslation()
  const [loading, setLoading] = useState(false)
  const [loader, setLoader] = useState(true)
  const dispatch = useAppDispatch()
  const {errors} = useAppSelector((state) => state.student)
  const [countries, setCountries] = useState([])
  const [courses, setcourses] = useState<any>([])
  const [programLevel, setProgramLevel] = useState([])
  const [consents, setConsents] = useState<Record<ConsentKey, boolean>>({...initialConsents})
  const [successMsg, setSuccessMsg] = useState<boolean>(false)
  const [firstName, setFirstName] = useState<any>('')
  const [gender, setGender] = useState<any>()

  const allConsentsChecked = Object.values(consents).every(Boolean)

  const handleConsentChange = (key: ConsentKey) => {
    setConsents((previous) => ({
      ...previous,
      [key]: !previous[key],
    }))
  }

  // Validation
  const validationSchema = Yup.object().shape({
    prefix: Yup.string().required(t('VALIDATION.SELECT_', {name: t('LABEL.PREFIX')})),
    first_name: Yup.string()
      .matches(/^[A-Za-zÀ-ÖØ-öø-ÿ\s'’]+$/, t('VALIDATION.ONLY_LETTERS'))
      .required(t('VALIDATION.REQUIRED_', {name: t('LABEL.FIRST_NAME')})),
    last_name: Yup.string()
      .matches(/^[A-Za-zÀ-ÖØ-öø-ÿ\s'’]+$/, t('VALIDATION.ONLY_LETTERS'))
      .required(t('VALIDATION.REQUIRED_', {name: t('LABEL.LAST_NAME')})),
    gender_id: Yup.string().required(t('VALIDATION.SELECT_', {name: t('LABEL.GENDER')})),
    birth_date: Yup.string()
      .trim()
      .required(t('VALIDATION.SELECT_', {name: t('LABEL.BIRTH_DATE')})),
    contact_number: Yup.string()
      .required(t('VALIDATION.REQUIRED_', {name: t('LABEL.CONTACT_NUMBER')}))
      .matches(/^\+[1-9]\d{7,14}$/, t('VALIDATION.INVALID_NUMBER')),
    email: Yup.string()
      .required(t('VALIDATION.REQUIRED_', {name: t('PROFILE.EMAIL')}))
      .email(t('PROFILE.EMAIL_FORMAT')),
    nationality_id: Yup.string().required(t('VALIDATION.SELECT_', {name: t('LABEL.NATIONALITY')})),
    country_id: Yup.string().required(t('VALIDATION.SELECT_', {name: t('LABEL.STD_COUNTRY')})),
    course_id: Yup.string().required(t('VALIDATION.SELECT_', {name: t('LABEL.COURSE')})),
    how_did_you_hear: Yup.string().required(
      t('VALIDATION.SELECT_', {name: t('LABEL.HOW_DID_YOU_HEAR')})
    ),
    education_level_id: Yup.string().required(
      t('VALIDATION.SELECT_', {name: t('LABEL.EDUCATION_LEVEL')})
    ),
  })

  const formik = useFormik({
    initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values, {setSubmitting, resetForm}) => {
      if (!allConsentsChecked) {
        setSubmitting(false)
        return
      }

      setLoading(true)
      const formData = new FormData()
      formData.append('campus_id', '17')
      formData.append('language', i18n.language)
      setFirstName(values.first_name)
      setGender(values.gender_id)
      Object.entries(values).forEach(([key, val]) => {
        formData.append(key, val as string)
      })

      Object.entries(consents).forEach(([key, accepted]) => {
        formData.append(`consent_${key}`, String(accepted))
      })

      dispatch(StoreOnlineCourseApp(formData)).then((res: any) => {
        if (res.meta.requestStatus === 'fulfilled') {
          resetForm()
          setConsents({...initialConsents})
          setSuccessMsg(true)
        } else if (res.meta.requestStatus === 'rejected') {
          showErrorMessage(
            res.payload === 'duplicate'
              ? t('VALIDATION.DUPLICATE_RECORD')
              : t('MESSAGES.SERVER_ERROR')
          )
        }
        setLoading(false)
        setSubmitting(false)
      })
    },
  })

  useEffect(() => {
    dispatch(GetOnlineCourses({lang: i18n.language, cid: 17})).then((res) => {
      if (res.meta.requestStatus === 'fulfilled') {
        setcourses(res.payload.courses)
        setCountries(res.payload.countries)
        setProgramLevel(res.payload.program_levels)
        setLoader(false)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleChangeLanguage = (lang) => {
    i18n.changeLanguage(lang)
    window.location.reload()
  }

  const languages = [
    {
      lang: 'fr',
      nameFr: 'French',
      nameEn: 'French',
      flag: toAbsoluteUrl('/media/flags/france.svg'),
    },
    {
      lang: 'en',
      nameFr: 'English',
      nameEn: 'English',
      flag: toAbsoluteUrl('/media/flags/united-states.svg'),
    },
  ]

  const currentLanguage = languages.find((x) => x.lang === i18n.language)
  const Gendrs = i18n.language === 'fr' ? GendersFr : Genders

  const consentItems: Array<{key: ConsentKey; text: string}> = [
    {
      key: 'accurateInformation',
      text: t('ONLINE.accurateInformation'),
    },
    {
      key: 'personalDataProcessing',
      text: t('ONLINE.personalDataProcessing'),
    },
    {
      key: 'enrolmentTerms',
      text: t('ONLINE.enrolmentTerms'),
    },
    {
      key: 'equipmentAndInternet',
      text: t('ONLINE.equipmentAndInternet'),
    },
  ]

  if (loader) return <Loader />

  if (successMsg) return <ShowSuccessMsg firstName={firstName} gender={gender} />

  return (
    <div className='card mb-2'>
      <div className='card-header d-flex flex-nowrap align-items-center gap-2 px-3 px-sm-6'>
        <div className='card-title m-0 flex-grow-1' style={{minWidth: 0}}>
          <img
            src={toAbsoluteUrl('/media/logo/dakar-logo.png')}
            alt='Logo'
            style={{maxWidth: '140px'}}
          />
        </div>

        <div className='d-flex flex-shrink-0 align-items-center gap-1 py-1'>
          {languages.map((l) => (
            <button
              key={l.lang}
              type='button'
              onClick={() => handleChangeLanguage(l.lang)}
              aria-label={currentLanguage?.lang === 'fr' ? l.nameFr : l.nameEn}
              title={currentLanguage?.lang === 'fr' ? l.nameFr : l.nameEn}
              className={`btn d-flex align-items-center justify-content-center gap-1 ${
                currentLanguage?.lang === l.lang
                  ? 'btn-primary text-white'
                  : 'bg-white text-dark border'
              }`}
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                borderRadius: '20px',
                minHeight: '30px',
                boxShadow:
                  currentLanguage?.lang === l.lang
                    ? '0 3px 10px rgba(64, 110, 137, 0.25)'
                    : '0 2px 6px rgba(0,0,0,0.08)',
                transition: 'all 0.2s ease',
              }}
            >
              <img
                src={l.flag}
                alt={l.lang}
                style={{
                  width: '20px',
                  height: '14px',
                  objectFit: 'cover',
                  borderRadius: '3px',
                }}
              />

              <span className='fw-semibold d-none d-sm-inline' style={{fontSize: '11px'}}>
                {currentLanguage?.lang === 'fr' ? l.nameFr : l.nameEn}
              </span>
            </button>
          ))}
        </div>
      </div>
      <div className='card-body p-3 p-md-6'>
        <form
          className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
          noValidate
          autoComplete='off'
          onSubmit={formik.handleSubmit}
        >
          <div className='row g-4 shadow-sm mx-0 mb-5 p-3 p-md-5'>
            <h3 className='fw-bolder'>{t('ONLINE.ONLINE_COURSE_TITLE')}</h3>
            <hr />
            <p className='fw-bold text-primary'>
              {i18n.language === 'fr' ? (
                <>
                  Veuillez compléter ce formulaire afin de soumettre votre demande d’inscription à
                  une formation en ligne.
                  <br />
                  Nous vous invitons à renseigner avec exactitude l’ensemble des informations
                  demandées. Les champs signalés par un astérisque (*) sont obligatoires.
                  <br />
                  Avant de soumettre votre demande, merci de vérifier attentivement les informations
                  saisies.
                </>
              ) : (
                <>
                  Please complete this form to submit your application for an online course. <br />
                  Please ensure that all information provided is accurate. Fields marked with an
                  asterisk (*) are mandatory.
                  <br />
                  Before submitting your application, please review all the information carefully.
                </>
              )}
            </p>
            <hr />
            <div className='row'>
              <div className='col-sm-6 col-md-4'>
                <label className='col-form-label fw-bolder required'>
                  {t('LABEL.HOW_DID_YOU_HEAR')}
                </label>
                <CustomSelect
                  name='how_did_you_hear'
                  className='input'
                  onChange={(hear: any) => {
                    formik.setFieldValue('how_did_you_hear', hear.id)
                  }}
                  value={formik.values.how_did_you_hear}
                  options={hearAbout}
                  placeholderText={t('LABEL.HOW_DID_YOU_HEAR')}
                />
                {formik.touched.how_did_you_hear && formik.errors.how_did_you_hear && (
                  <span className='text-danger'>{formik.errors.how_did_you_hear}</span>
                )}
              </div>
              <div className='col-sm-6 col-md-4'>
                <label className='col-form-label fw-bolder required'>{t('LABEL.PREFIX')}</label>
                <CustomSelect
                  name='prefix'
                  className='input'
                  onChange={(prefix: any) => {
                    formik.setFieldValue('prefix', prefix.id)
                    if (prefix.id === 'Mr.') {
                      formik.setFieldValue('gender_id', 1)
                    } else if (prefix.id === 'Mrs.' || prefix.id === 'Ms.') {
                      formik.setFieldValue('gender_id', 2)
                    } else {
                      formik.setFieldValue('gender_id', '')
                    }
                  }}
                  value={formik.values.prefix}
                  options={Prefix}
                  placeholderText={t('LABEL.PREFIX')}
                />
                {formik.touched.prefix && formik.errors.prefix && (
                  <span className='text-danger'>{formik.errors.prefix}</span>
                )}
              </div>
              <div className='col-sm-6 col-md-4'>
                <label className='col-form-label fw-bolder required'>{t('LABEL.GENDER')}</label>
                <CustomSelect
                  name='gender_id'
                  className='input'
                  onChange={(gender_id: any) => formik.setFieldValue('gender_id', gender_id.id)}
                  value={formik.values.gender_id}
                  options={Gendrs}
                  placeholderText={t('LABEL.GENDER')}
                />
                {formik.touched.gender_id && formik.errors.gender_id && (
                  <span className='text-danger'>{formik.errors.gender_id}</span>
                )}
              </div>

              <div className='col-sm-6 col-md-4'>
                <label className='col-form-label fw-bolder required'>{t('LABEL.FIRST_NAME')}</label>
                <input
                  type='text'
                  placeholder={t('LABEL.FIRST_NAME')}
                  {...formik.getFieldProps('first_name')}
                  className={clsx(
                    'form-control form-control-sm text-capitalize',
                    {
                      'is-invalid':
                        (formik.touched.first_name && formik.errors.first_name) ||
                        errors.first_name,
                    },
                    {
                      'is-valid': formik.touched.first_name && !formik.errors.first_name,
                    }
                  )}
                />
                {formik.touched.first_name && formik.errors.first_name && (
                  <span className='text-danger'>{formik.errors.first_name}</span>
                )}
              </div>
              <div className='col-sm-6 col-md-4'>
                <label className='col-form-label fw-bolder required'>{t('LABEL.LAST_NAME')}</label>
                <input
                  type='text'
                  placeholder={t('LABEL.LAST_NAME')}
                  {...formik.getFieldProps('last_name')}
                  className={clsx(
                    'form-control form-control-sm text-uppercase',
                    {
                      'is-invalid':
                        (formik.touched.last_name && formik.errors.last_name) || errors.last_name,
                    },
                    {
                      'is-valid': formik.touched.last_name && !formik.errors.last_name,
                    }
                  )}
                />
                {formik.touched.last_name && formik.errors.last_name && (
                  <span className='text-danger'>{formik.errors.last_name}</span>
                )}
              </div>
              <div className='col-sm-6 col-md-4'>
                <label className='col-form-label fw-bolder required'>
                  {t('LABEL.NATIONALITY')}
                </label>
                <CustomSelect
                  name='nationality_id'
                  className='input'
                  onChange={(nationality: any) =>
                    formik.setFieldValue('nationality_id', nationality.id)
                  }
                  value={formik.values.nationality_id}
                  options={countries}
                  placeholderText={t('LABEL.NATIONALITY')}
                />
                {formik.touched.nationality_id && formik.errors.nationality_id && (
                  <span className='text-danger'>{formik.errors.nationality_id}</span>
                )}
              </div>

              <div className='col-sm-6 col-md-4'>
                <label className='col-form-label fw-bolder required'>{t('LABEL.BIRTH_DATE')}</label>
                <DatePicker
                  calendar={gregorian}
                  containerStyle={{width: '100%'}}
                  value={formik.values.birth_date}
                  placeholder={t('LABEL.BIRTH_DATE')}
                  style={{
                    width: '100%',
                    height: '40px',
                    boxSizing: 'border-box',
                  }}
                  onChange={(e: any) => {
                    if (e) {
                      formik.setFieldValue('birth_date', `${e.day}-${e.month.number}-${e.year}`)
                    } else {
                      formik.setFieldValue('birth_date', '')
                    }
                  }}
                  format='DD-MM-YYYY'
                  onOpenPickNewDate={false}
                  maxDate={new DateObject()}
                />

                {formik.touched.birth_date && formik.errors.birth_date && (
                  <span className='text-danger'>{formik.errors.birth_date}</span>
                )}
              </div>
              <div className='col-sm-6 col-md-4'>
                <label className='col-form-label fw-bolder required'>
                  {t('LABEL.CONTACT_NUMBER')}
                </label>
                <input
                  type='text'
                  placeholder={t('LABEL.CONTACT_NUMBER')}
                  {...formik.getFieldProps('contact_number')}
                  className={clsx(
                    'form-control form-control-sm text-capitalize',
                    {
                      'is-invalid': formik.touched.contact_number && formik.errors.contact_number,
                    },
                    {
                      'is-valid': formik.touched.contact_number && !formik.errors.contact_number,
                    }
                  )}
                />
                {formik.touched.contact_number && formik.errors.contact_number && (
                  <span className='text-danger'>{formik.errors.contact_number}</span>
                )}
              </div>
              <div className='col-sm-6 col-md-4'>
                <label className='col-form-label fw-bolder required'>{t('PROFILE.EMAIL')}</label>
                <input
                  type='email'
                  placeholder={t('PROFILE.EMAIL')}
                  {...formik.getFieldProps('email')}
                  className={clsx(
                    'form-control form-control-sm',
                    {
                      'is-invalid': (formik.touched.email && formik.errors.email) || errors.email,
                    },
                    {
                      'is-valid': formik.touched.email && !formik.errors.email,
                    }
                  )}
                />
                {formik.touched.email && formik.errors.email && (
                  <span className='text-danger'>{formik.errors.email}</span>
                )}
              </div>

              <div className='col-sm-6 col-md-4'>
                <label className='col-form-label label fw-bolder required'>
                  {t('LABEL.EDUCATION_LEVEL')}
                </label>
                <CustomSelect
                  name='education_level_id'
                  className='input'
                  value={formik.values.education_level_id}
                  onChange={(obj: any) => formik.setFieldValue('education_level_id', obj.id)}
                  options={programLevel}
                  placeholderText={t('LABEL.EDUCATION_LEVEL')}
                />
                {formik.touched.education_level_id && formik.errors.education_level_id && (
                  <span className='text-danger fw-bold'>{formik.errors.education_level_id}</span>
                )}
              </div>
              <div className='col-sm-6 col-md-4'>
                <label className='col-form-label fw-bolder required'>
                  {t('LABEL.STD_COUNTRY')}
                </label>
                <CustomSelect
                  name='country_id'
                  className='input'
                  onChange={(country_id: any) => formik.setFieldValue('country_id', country_id.id)}
                  value={formik.values.country_id}
                  options={countries}
                  placeholderText={t('LABEL.STD_COUNTRY')}
                />
                {formik.touched.country_id && formik.errors.country_id && (
                  <span className='text-danger'>{formik.errors.country_id}</span>
                )}
              </div>
              <div className='col-sm-6 col-md-4'>
                <label className='col-form-label fw-bolder required'>{t('LABEL.COURSE')}</label>
                <CustomSelect
                  name='course_id'
                  className='input'
                  onChange={(obj) => formik.setFieldValue('course_id', obj.id)}
                  value={formik.values.course_id}
                  options={courses}
                  placeholderText={t('LABEL.COURSE')}
                />
                {formik.touched.course_id && formik.errors.course_id && (
                  <span className='text-danger'>{formik.errors.course_id}</span>
                )}
              </div>
            </div>
          </div>

          {/* Declarations and Consent */}
          <div className='row shadow-sm mx-0 mt-5 p-3 p-md-5'>
            <div className='col-12'>
              <h4 className='fw-bolder mb-4 text-primary'>{t('ONLINE.DECLARATIONS')}</h4>

              {consentItems.map(({key, text}) => (
                <div className='form-check d-flex align-items-start gap-3 ps-0 mb-3' key={key}>
                  <input
                    id={`consent-${key}`}
                    className='form-check-input flex-shrink-0 m-0 mt-1'
                    type='checkbox'
                    checked={consents[key]}
                    onChange={() => handleConsentChange(key)}
                  />
                  <label className='form-check-label lh-base' htmlFor={`consent-${key}`}>
                    {text}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Submit Row */}
          <div className='row shadow-sm mx-0 mt-5 p-3 p-md-5'>
            <div className='col-12 col-sm-auto'>
              <button
                type='submit'
                className='btn btn-sm btn-light btn-primary fw-bolder w-100'
                disabled={formik.isSubmitting || loading || !allConsentsChecked}
              >
                {!loading && (
                  <span className='indicator-label'>
                    <span className='fas fa-paper-plane mx-1'></span>
                    {t('BUTTONS.SUBMIT_APP')}
                  </span>
                )}
                {loading && (
                  <span className='indicator-progress' style={{display: 'block'}}>
                    {t('GLOBAL.LOADING')}
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default SenegalCourseApplication
